/** Legacy web3 wallet - can be replaced by EIP-6963 */
export const softwareWeb3Wallets = [
  // Generic / fallback web3 wallet
  'softwareWeb3',
] as const;

/**
 * Wallet Discovery Protocol EIP-6963
 * https://eip6963.org/
 */
const discoveredWallets = [
  'softwareDiscovered',
  // Always displayed even if not discovered
  'softwareDiscovered_metaMask',
  'softwareDiscovered_trustWallet',
] as const;

const SUPPORTED_WALLETS = {
  // hardware: ['trezor', 'ledger'],
  /** https://eips.ethereum.org/EIPS/eip-6963 */
  softwareDiscovered: discoveredWallets,
  software: [
    ...softwareWeb3Wallets,
    'coinbaseWallet',
    'walletConnect',
    'qrSynced',
  ],
  key: ['privateKey', 'keystore', 'delegatedKey'],
  empty: [null],
} as const;

export const EMPTY_WALLET: WalletShapes['empty'] = Object.freeze({
  updated: undefined,
  walletType: null,
  walletAddress: '',
  walletAddressOriginal: '',
});

const WALLET_KINDS = Object.keys(
  SUPPORTED_WALLETS
) as (keyof typeof SUPPORTED_WALLETS)[];

type WalletKind = keyof typeof SUPPORTED_WALLETS;

type SupportedWallets = {
  [K in WalletKind]: (typeof SUPPORTED_WALLETS)[K][number];
};

type WalletShapes = {
  // hardware: Readonly<{
  //   updated?: number;
  //   walletType: SupportedWallets['hardware'];
  //   /** Lowercase wallet address */
  //   walletAddress: string;
  //   walletAddressOriginal: string;
  //   hardwareWalletPath: string;
  //   privateKeyEncrypted?: undefined;
  //   privateKeyPersonalEncryption?: undefined;
  //   personalWalletSecret?: string | undefined;
  //   walletDiscoveryRdns?: undefined
  // }>;

  /** Software wallet discovered by rdns https://eips.ethereum.org/EIPS/eip-6963 */
  softwareDiscovered: Readonly<{
    updated?: number;
    /** Metamask, Trust Wallet, Toshi, BLUE Wallet, Coinbase */
    walletType: SupportedWallets['softwareDiscovered'];
    walletAddress: string;
    walletAddressOriginal: string;
    /** https://eips.ethereum.org/EIPS/eip-6963 */
    walletDiscoveryRdns: string;
    hardwareWalletPath?: undefined;
    privateKeyEncrypted?: undefined;
    privateKeyPersonalEncryption?: undefined;
    personalWalletSecret?: string | undefined;
  }>;
  software: Readonly<{
    updated?: number;
    /** Metamask, Trust Wallet, Toshi, BLUE Wallet, Coinbase */
    walletType: SupportedWallets['software'];
    walletAddress: string;
    walletAddressOriginal: string;
    hardwareWalletPath?: undefined;
    privateKeyEncrypted?: undefined;
    privateKeyPersonalEncryption?: undefined;
    personalWalletSecret?: string | undefined;
    walletDiscoveryRdns?: undefined;
  }>;
  key: Readonly<{
    updated?: number;
    walletType: Exclude<SupportedWallets['key'], 'delegatedKey'>;
    walletAddress: string;
    walletAddressOriginal: string;
    /** Encrypted JSON wallet, see `unlockByKeystoreFile` function. */
    privateKeyEncrypted: string;
    privateKeyPersonalEncryption?: 'unlock' | 'personal1';
    privateKeyPersonalSecret?: string;
    hardwareWalletPath?: undefined;
    personalWalletSecret?: string | undefined;
    walletDiscoveryRdns?: undefined;
  }>;
  empty: Readonly<{
    updated?: number;
    walletType: SupportedWallets['empty'];
    walletAddress: '';
    walletAddressOriginal: '';
    hardwareWalletPath?: undefined;
    privateKeyEncrypted?: undefined;
    privateKeyPersonalEncryption?: undefined;
    personalWalletSecret?: string | undefined;
    walletDiscoveryRdns?: undefined;
  }>;
};

export type Wallet = WalletShapes[keyof WalletShapes];

export type WalletType = Exclude<
  NonNullable<SupportedWallets[WalletKind]>,
  'delegatedKey'
>;

export type WalletShape = {
  updated?: number;
  walletType: undefined | null | WalletType;
  walletAddress: undefined | string;
  walletAddressOriginal: undefined | string;
  privateKeyEncrypted?: undefined | string;
  delegatedKeyEncrypted?: undefined | string;
  /**
   * Levels of encryption:
   *  undefined - legacy, AES encrypted with hardcoded static key (insecure)
   *  1 - same encryption as legacy, but used for wallet unlock (until we associate wallet and get personal wallet secret for next levels)
   *  2 - standard AES personal wallet secret encryption (all users should be auto-migrated to thisone)
   */
  privateKeyPersonalEncryption?: 'unlock' | 'personal1';
  personalWalletSecret?: undefined | string;
  hardwareWalletPath?: undefined | string;
  walletDiscoveryRdns?: undefined | string;
};

function isWalletKind<K extends WalletKind>(
  kind: K,
  wallet: WalletShape
): wallet is WalletShapes[K] {
  switch (kind) {
    case 'empty': {
      return (
        SUPPORTED_WALLETS.empty.includes(
          wallet.walletType as SupportedWallets['empty']
        ) &&
        !wallet.walletAddress &&
        !wallet.hardwareWalletPath &&
        !wallet.privateKeyEncrypted
      );
    }
    // case 'hardware':
    //   return (
    //     SUPPORTED_WALLETS.hardware.includes(
    //       wallet.walletType as SupportedWallets['hardware']
    //     ) &&
    //     !!wallet.walletAddress &&
    //     !!wallet.hardwareWalletPath &&
    //     !wallet.privateKeyEncrypted
    //   );
    case 'softwareDiscovered':
      return (
        SUPPORTED_WALLETS.softwareDiscovered.includes(
          wallet.walletType as SupportedWallets['softwareDiscovered']
        ) &&
        !!wallet.walletDiscoveryRdns &&
        !!wallet.walletAddress &&
        !wallet.hardwareWalletPath &&
        !wallet.privateKeyEncrypted
      );
    case 'software':
      return (
        SUPPORTED_WALLETS.software.includes(
          wallet.walletType as SupportedWallets['software']
        ) &&
        !!wallet.walletAddress &&
        !wallet.hardwareWalletPath &&
        !wallet.privateKeyEncrypted
      );
    case 'key':
      return (
        SUPPORTED_WALLETS.key.includes(
          wallet.walletType as SupportedWallets['key']
        ) &&
        !!wallet.walletAddress &&
        (!!wallet.privateKeyEncrypted || !!wallet.delegatedKeyEncrypted) &&
        !wallet.hardwareWalletPath
      );
    default: {
      const _exhaustiveCheck: never = kind;
    }
  }
  return false;
}

/**
 * A type guard which takes a wallet-like object and
 */
export function isWallet(wallet: WalletShape): wallet is Wallet {
  if (WALLET_KINDS.some(type => isWalletKind(type, wallet))) {
    return true;
  }
  return false;
}

/** @deprecated We only support Software wallets now */
export const isWalletWithoutConfirmation = (
  walletType: WalletShape['walletType']
) =>
  !!walletType &&
  SUPPORTED_WALLETS.key.includes(walletType as SupportedWallets['key']);
